import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import type { ProjectStep } from "../hooks/useProjectNextStep";

interface Props {
  steps: ProjectStep[];
  nextStep: ProjectStep | null;
  doneCount: number;
  total: number;
  loading: boolean;
  /** Anropas när steget inte har en egen sida utan skapas i verktygsblocket */
  onOpenTools: (phase: "basp" | "basu") => void;
}

/** Max antal åtgärder som visas – fler blir en lista att scrolla i stället för en plan. */
const MAX_ACTIONS = 5;

export const NextStepPanel: React.FC<Props> = ({
  steps,
  nextStep,
  doneCount,
  total,
  loading,
  onOpenTools,
}) => {
  const navigate = useNavigate();

  if (loading) return null;

  const percent = total > 0 ? Math.round((doneCount / total) * 100) : 0;
  const todo = steps.filter((s) => !s.done).slice(0, MAX_ACTIONS);
  const done = steps.filter((s) => s.done);

  const go = (step: ProjectStep) => {
    if (step.path) navigate(step.path);
    else onOpenTools(step.phase);
  };

  return (
    <Card className="p-6 border-primary/25 bg-primary/5">
      <div className="flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
          {nextStep ? "Att göra nu" : "Klart"}
        </span>
        <h2 className="text-xl font-semibold text-foreground">
          {nextStep
            ? `${todo.length} ${todo.length === 1 ? "sak" : "saker"} väntar på dig`
            : "Alla grundläggande dokument är påbörjade"}
        </h2>
        {!nextStep && (
          <p className="text-sm text-muted-foreground">
            Fortsätt fylla på med arbetsberedningar, skyddsronder och uppföljning under projektets gång.
          </p>
        )}
      </div>

      {todo.length > 0 && (
        <ol className="mt-5 space-y-2">
          {todo.map((step, index) => (
            <li
              key={step.key}
              className={cn(
                "flex flex-col gap-2 rounded-xl border bg-background/80 p-3 sm:flex-row sm:items-center sm:gap-4",
                index === 0 ? "border-primary/40" : "border-border/60"
              )}
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-foreground">{step.title}</span>
                  {step.docNumber && (
                    <span className="text-[11px] text-muted-foreground">{step.docNumber}</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{step.why}</p>
              </div>
              <Button
                size="sm"
                variant={index === 0 ? "default" : "outline"}
                className="w-full sm:w-auto"
                onClick={() => go(step)}
              >
                {index === 0 ? "Fortsätt" : "Öppna"}
                <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ol>
      )}

      <div className="mt-6 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-foreground">
            {doneCount} av {total} klara
          </span>
          <span className="text-muted-foreground">{percent}%</span>
        </div>
        <Progress value={percent} className="h-2" />
      </div>

      {done.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
          {done.map((step) => (
            <li key={step.key} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
              {step.title}
            </li>
          ))}
        </ul>
      )}

      <Button
        variant="link"
        size="sm"
        className="mt-3 px-0 text-muted-foreground hover:text-foreground"
        onClick={() => navigate("/processguide")}
      >
        Så hänger stegen ihop – se hela processen
      </Button>
    </Card>
  );
};

export default NextStepPanel;
